/**
 * Flow + FlowCategory API hooks. Maps to apps/flows/views.py.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  CallLogEntry,
  CallLogWriteInput,
  CallOutcome,
  Flow,
  FlowCategory,
  FlowCategoryInput,
  FlowGraph,
  FlowGraphWriteInput,
  FlowInput,
  FlowPersonalizationMap,
  FlowStepOrder,
  NodePersonalization,
  NodePersonalizationInput,
  Paginated,
  PublishError,
} from "@/types/api";

const CATEGORIES_KEY = ["flow-categories"];
const FLOWS_KEY = ["flows"];

// ---------- Categories ----------

export function useCategories() {
  return useQuery<Paginated<FlowCategory>>({
    queryKey: CATEGORIES_KEY,
    queryFn: async () => {
      const r = await api.get<Paginated<FlowCategory>>("/v1/flows/categories/");
      return r.data;
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation<FlowCategory, unknown, FlowCategoryInput>({
    mutationFn: async (input) => {
      const r = await api.post<FlowCategory>("/v1/flows/categories/", input);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY }),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.delete(`/v1/flows/categories/${id}/`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY }),
  });
}

// ---------- Flows ----------

export function useFlows() {
  return useQuery<Paginated<Flow>>({
    queryKey: FLOWS_KEY,
    queryFn: async () => {
      const r = await api.get<Paginated<Flow>>("/v1/flows/");
      return r.data;
    },
  });
}

export function useCreateFlow() {
  const queryClient = useQueryClient();
  return useMutation<Flow, unknown, FlowInput>({
    mutationFn: async (input) => {
      const r = await api.post<Flow>("/v1/flows/", input);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FLOWS_KEY }),
  });
}

export function useUpdateFlow(flowId: string) {
  const queryClient = useQueryClient();
  return useMutation<Flow, unknown, FlowInput>({
    mutationFn: async (input) => {
      const r = await api.put<Flow>(`/v1/flows/${flowId}/`, input);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FLOWS_KEY });
      queryClient.invalidateQueries({ queryKey: ["flow-graph", flowId] });
    },
  });
}

export function useFlowGraph(flowId: string | undefined) {
  return useQuery<FlowGraph>({
    queryKey: ["flow-graph", flowId],
    queryFn: async () => {
      const r = await api.get<FlowGraph>(`/v1/flows/${flowId}/graph/`);
      return r.data;
    },
    enabled: !!flowId,
  });
}

export function useSaveFlowGraph(flowId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<FlowGraph, unknown, FlowGraphWriteInput>({
    mutationFn: async (input) => {
      const r = await api.put<FlowGraph>(`/v1/flows/${flowId}/graph/`, input);
      return r.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["flow-graph", flowId], data);
    },
  });
}

export function usePublishFlow() {
  const queryClient = useQueryClient();
  return useMutation<Flow, { response?: { data: PublishError } }, string>({
    mutationFn: async (flowId) => {
      const r = await api.post<Flow>(`/v1/flows/${flowId}/publish/`);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FLOWS_KEY }),
  });
}

export function useDeleteFlow() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (flowId) => {
      await api.delete(`/v1/flows/${flowId}/`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FLOWS_KEY }),
  });
}

export function useUnpublishFlow() {
  const queryClient = useQueryClient();
  return useMutation<Flow, unknown, string>({
    mutationFn: async (flowId) => {
      const r = await api.post<Flow>(`/v1/flows/${flowId}/unpublish/`);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FLOWS_KEY }),
  });
}

// ---------- Staff personalization ----------
//
// A Staff member's own wording for a step — private to them (see
// apps/flows/views.py::FlowViewSet.personalization). Scoped to the
// requesting flow + the caller's own account server-side; nothing here
// ever exposes or accepts another user's overrides.

function personalizationKey(flowId: string | undefined) {
  return ["flow-personalization", flowId];
}

export function useFlowPersonalization(flowId: string | undefined) {
  return useQuery<FlowPersonalizationMap>({
    queryKey: personalizationKey(flowId),
    queryFn: async () => {
      const r = await api.get<FlowPersonalizationMap>(`/v1/flows/${flowId}/personalization/`);
      return r.data;
    },
    enabled: !!flowId,
  });
}

export function useSaveNodePersonalization(flowId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<NodePersonalization | null, unknown, NodePersonalizationInput>({
    mutationFn: async (input) => {
      // Blank in both languages is the same as "not personalized" — the
      // API deletes the override and returns 204 rather than a body.
      const r = await api.put<NodePersonalization>(`/v1/flows/${flowId}/personalization/`, input);
      return r.status === 204 ? null : r.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<FlowPersonalizationMap>(personalizationKey(flowId), (prev) => {
        const next = { ...(prev ?? {}) };
        if (data) {
          next[variables.node_id] = data;
        } else {
          delete next[variables.node_id];
        }
        return next;
      });
    },
  });
}

/** "Reset to script" — wording-only (see PersonalizedSpiel). Mirrors the
 * backend's DELETE handler (apps/flows/views.py::FlowViewSet
 * .personalization): if this node also has a saved branch order
 * (edge_order), that survives untouched and only the wording clears;
 * otherwise the whole entry is gone. The response body is empty either
 * way, so which one happened is inferred from the cached entry itself
 * rather than the response. */
export function useResetNodePersonalization(flowId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (nodeId) => {
      await api.delete(`/v1/flows/${flowId}/personalization/`, { data: { node_id: nodeId } });
    },
    onSuccess: (_data, nodeId) => {
      queryClient.setQueryData<FlowPersonalizationMap>(personalizationKey(flowId), (prev) => {
        if (!prev || !prev[nodeId]) return prev;
        const { edge_order } = prev[nodeId];
        if (edge_order.length > 0) {
          return { ...prev, [nodeId]: { spiel_text: "", spiel_text_es: "", edge_order } };
        }
        const next = { ...prev };
        delete next[nodeId];
        return next;
      });
    },
  });
}

// ---------- Staff personal step order ----------
//
// One Staff member's own preferred order across an entire flow's steps —
// private to them (see apps/flows/views.py::FlowViewSet.step_order).
// Distinct from node personalization above (that's per-node wording/branch
// order; this is per-flow, the order steps are approached in).

function stepOrderKey(flowId: string | undefined) {
  return ["flow-step-order", flowId];
}

export function useFlowStepOrder(flowId: string | undefined) {
  return useQuery<FlowStepOrder>({
    queryKey: stepOrderKey(flowId),
    queryFn: async () => {
      const r = await api.get<FlowStepOrder>(`/v1/flows/${flowId}/step-order/`);
      return r.data;
    },
    enabled: !!flowId,
  });
}

export function useSaveFlowStepOrder(flowId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<FlowStepOrder, unknown, string[]>({
    mutationFn: async (node_order) => {
      const r = await api.put<FlowStepOrder>(`/v1/flows/${flowId}/step-order/`, { node_order });
      return r.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<FlowStepOrder>(stepOrderKey(flowId), data);
    },
  });
}

export function useResetFlowStepOrder(flowId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      await api.delete(`/v1/flows/${flowId}/step-order/`);
    },
    onSuccess: () => {
      queryClient.setQueryData<FlowStepOrder>(stepOrderKey(flowId), { node_order: [] });
    },
  });
}

// ---------- Call log ----------
//
// Written by Staff (via FlowPlayerPage) at the end of every run through a
// script — normal completion or an early end. Read by Admin only.

/** Staff-side: log the just-finished/just-ended call. Fire-and-forget is
 * fine from the caller (see FlowPlayerPage) — a failure here shouldn't
 * block Staff from moving on to the next call, so this deliberately has
 * no cache to invalidate for the Staff side (they never read call logs
 * back — only Admin's CallLogPage does, via useCallLogs below). */
export function useCreateCallLog(flowId: string | undefined) {
  return useMutation<CallLogEntry, unknown, CallLogWriteInput>({
    mutationFn: async (input) => {
      const r = await api.post<CallLogEntry>(`/v1/flows/${flowId}/call-logs/`, input);
      return r.data;
    },
  });
}

/** Admin-only browse/filter view — apps/flows/views.py::CallLogViewSet. */
export function useCallLogs(filters: { outcome?: CallOutcome; flowId?: string } = {}) {
  return useQuery<Paginated<CallLogEntry>>({
    queryKey: ["call-logs", filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.outcome) params.outcome = filters.outcome;
      if (filters.flowId) params.flow = filters.flowId;
      const r = await api.get<Paginated<CallLogEntry>>("/v1/flows/call-logs/", { params });
      return r.data;
    },
  });
}
